import type { ThinkingStep, ToolCall } from '@/hooks/useChat';

const CONTEXT_FULL_ERROR_CODE = 'context_full';
const CONTEXT_FULL_ERROR_PREFIX = 'The context window is full';

const parseToolOutput = (value?: unknown): Record<string, unknown> | null => {
  if (!value) return null;
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
};

// Leftover packing still returns a paired tool output so the model can stop
// fetching. That is not a broken tool, and the work trail should not show it.
export const toolCallIsContextFull = (
  tool?: Pick<ToolCall, 'function'>,
): boolean => {
  const output = parseToolOutput(tool?.function?.output);
  if (!output) return false;
  if (output.error_code === CONTEXT_FULL_ERROR_CODE) return true;
  return typeof output.error === 'string'
    && output.error.startsWith(CONTEXT_FULL_ERROR_PREFIX);
};

const READ_TOOL_FAMILY = new Set([
  'plan_document_access',
  'read_files',
  'delegate_document_analysis',
]);

const HEADER_TOOL_PRIORITY = ['read_files', 'delegate_document_analysis'];

export const getToolFamily = (
  toolName?: string,
  callId?: string,
  family?: string | null,
): string | null => {
  if (family) return family;
  if (!toolName) return null;
  if (READ_TOOL_FAMILY.has(toolName)) return 'read';
  if (isSpawnSpecialistTool(toolName) && callId) {
    return `${toolName}:${callId}`;
  }
  return toolName;
};

export const isSpawnSpecialistTool = (name?: string | null): boolean => (
  name === 'spawn_research' || name === 'spawn_web_research'
);

export const isSpawnSpecialistFamily = (family?: string | null): boolean => (
  Boolean(
    family?.startsWith('spawn_research:')
    || family?.startsWith('spawn_web_research:'),
  )
);

const getStepToolFamily = (step: ThinkingStep): string | null => {
  if (step.type !== 'tool') return null;
  const tool = step.data as ToolCall;
  return getToolFamily(tool.function?.name, tool.call_id, tool.family);
};

export const getActivityToolFamily = (steps: ThinkingStep[]): string | null => {
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    const family = getStepToolFamily(steps[index]);
    if (family) return family;
  }
  return null;
};

// A chip is live either because its own tools are still running, or because it
// is the end of the timeline and so the place the next step will land.
// ``isTimelineTail`` is about the whole timeline, not just the last activity: a
// chip with later text or waits beneath it has been overtaken, and shimmering
// there points at finished work while the real activity is further down.
export const activityChipIsLive = (
  steps: ThinkingStep[],
  isStreaming: boolean,
  isTimelineTail: boolean,
): boolean => {
  if (!isStreaming) return false;
  if (isTimelineTail) return true;
  return activityHasInFlightTool(steps, isStreaming);
};

export const shouldShowLeadWaitingChip = ({
  waitingFor,
  isStreaming,
  finalAnswerConfirmed,
  stopRequested,
}: {
  waitingFor?: string | null;
  isStreaming: boolean;
  finalAnswerConfirmed: boolean;
  stopRequested: boolean;
}): boolean => (
  waitingFor === 'specialists'
  && isStreaming
  && !finalAnswerConfirmed
  && !stopRequested
);

export const shouldShowLeadWaitedChip = (
  waitedDurationMs?: number | null,
  waitingFor?: string | null,
): boolean => (
  typeof waitedDurationMs === 'number'
  && waitedDurationMs > 0
  && !waitingFor
);

// Whether the end of a rendered list already shows something in progress.
//
// Being last in the timeline is not enough for a chip to count. Once its tools
// settle it renders as finished — past-tense label, no shimmer — so treating it
// as live because the next step will land there left nothing on screen saying
// the lead was still working, between a tool finishing and whatever it did
// next. A chip that has not run a tool yet is the exception: it labels itself
// "Thinking", and standing in beneath that would say the same thing twice.
const timelineTailIsLive = (items: InterleavedTurnItem[], isLive = false): boolean => {
  const tail = items[items.length - 1];
  if (!tail) return false;
  if (tail.kind === 'wait') return tail.waiting;
  if (tail.kind !== 'activity') return false;
  if (!tail.steps.some((step) => step.type === 'tool')) return true;
  return activityHasInFlightTool(tail.steps, isLive);
};

// The lead is working but nothing on screen represents it. Deliberately blind
// to what it did before or intends to do next: the model reorders spawning,
// reading and waiting freely, and any rule that encodes an expected sequence
// goes stale the first time it picks a different one.
//
// ``items`` must be what is rendered immediately above this chip, because the
// only question is whether the foot of the turn already shows something in
// progress. A live chip higher up does not answer it: once a wait resolves to
// "Waited for 8s" the lead goes quiet while it reads the report, and deferring
// to a chip further up left the turn looking finished when it was not.
export const shouldShowPendingLeadThinking = ({
  finalAnswerConfirmed,
  isStreaming,
  items,
  stopRequested,
}: {
  finalAnswerConfirmed: boolean;
  isStreaming: boolean;
  items: InterleavedTurnItem[];
  stopRequested: boolean;
}): boolean => {
  if (!isStreaming || finalAnswerConfirmed || stopRequested) return false;
  // Standing in beneath a chip that is already shimmering would say the same
  // thing twice.
  return !timelineTailIsLive(items, isStreaming);
};

const isLiveToolStep = (step: ThinkingStep, isLive = false): boolean => {
  if (step.type !== 'tool') return false;
  const tool = step.data as ToolCall;
  const status = tool.status;
  if (status === 'pending' || status === 'in_progress') return true;
  if (status === 'stopped' || status === 'failed') return false;
  // spawn_research / spawn_web_research return as soon as the specialist
  // starts. On a live turn that payload is still in flight; after the turn
  // ends it is not.
  if (!isLive || !isSpawnSpecialistTool(tool.function?.name)) return false;
  return parseToolOutput(tool.function?.output)?.status === 'running';
};

const activityHasInFlightTool = (steps: ThinkingStep[], isLive = false): boolean => (
  steps.some((step) => isLiveToolStep(step, isLive))
);

// `plan_document_access` never stands for work of its own. Its whole result is
// a decision about which documents to read, which is why the chip labels itself
// "Reading N documents" on the strength of it alone. It has not finished until
// that read lands.
const lastToolOnlyChoseWhatToDoNext = (steps: ThinkingStep[]): boolean => {
  const tools = steps.filter((step) => step.type === 'tool');
  const last = tools[tools.length - 1]?.data as ToolCall | undefined;
  return last?.function?.name === 'plan_document_access';
};

// Whether a chip's work is still going on, and so whether thought arriving now
// is part of it.
//
// A chip stands for one piece of work and the thinking that produced it, which
// is why a chip that has run nothing yet is simply "Thinking". Once its tools
// have settled the piece is over, and what the lead thinks next is about what
// to do after it — a different chip, however the previous one was labelled.
const activityIsOpen = (steps: ThinkingStep[]): boolean => {
  if (!steps.some((step) => step.type === 'tool')) return true;
  if (activityHasInFlightTool(steps)) return true;
  return lastToolOnlyChoseWhatToDoNext(steps);
};

export const getActivityHeaderTool = (steps: ThinkingStep[]): ToolCall | undefined => {
  const tools = steps
    .filter((step) => step.type === 'tool')
    .map((step) => step.data as ToolCall);

  for (const name of HEADER_TOOL_PRIORITY) {
    const match = [...tools].reverse().find((tool) => tool.function?.name === name);
    if (match) return match;
  }

  return tools[tools.length - 1];
};

export type TimelineTextInsertion = {
  afterStepIndex: number;
  isFinal?: boolean;
  messageId?: string;
  roundNum?: number;
  text: string;
};

export type TimelineWaitMarker = {
  id: string;
  round_num: number;
  waiting_for?: string;
  duration_ms?: number;
};

export const timelineWaitMarkersFromMetadata = (
  value: unknown,
  fallbackDurationMs?: number | null,
): TimelineWaitMarker[] => {
  if (Array.isArray(value)) {
    const markers = value.flatMap((item, index) => {
      if (!item || typeof item !== 'object') return [];
      const marker = item as Record<string, unknown>;
      const durationMs = Number(marker.duration_ms || 0);
      return durationMs > 0
        ? [{
            id: typeof marker.id === 'string' ? marker.id : `wait-${index + 1}`,
            round_num: Number(marker.round_num || 0),
            duration_ms: durationMs,
          }]
        : [];
    });
    if (markers.length > 0) return markers;
  }
  return typeof fallbackDurationMs === 'number' && fallbackDurationMs > 0
    ? [{
        id: 'wait-legacy',
        round_num: Number.MAX_SAFE_INTEGER,
        duration_ms: fallbackDurationMs,
      }]
    : [];
};

export type InterleavedTurnItem =
  | { kind: 'activity'; id: number; steps: ThinkingStep[] }
  | { kind: 'text'; id: number; isFinal?: boolean; messageId?: string; text: string }
  | { kind: 'wait'; id: string; waiting: boolean; durationMs?: number; steps: ThinkingStep[] };

export const isTimelineTokenContinuation = (previous: string, next: string): boolean => {
  if (!previous || !next) return false;
  if (/\s$/.test(previous) || /^\s/.test(next)) return false;
  return /[A-Za-z0-9]$/.test(previous) && /^['’a-z]/.test(next);
};

const shouldContinueTimelineText = (
  last: TimelineTextInsertion,
  afterStepIndex: number,
  roundNum?: number,
  messageId?: string,
): boolean => {
  if (messageId || last.messageId) return Boolean(messageId) && last.messageId === messageId;
  if (roundNum != null && last.roundNum === roundNum) return true;
  if (last.afterStepIndex !== afterStepIndex) return false;
  return last.roundNum == null || roundNum == null || last.roundNum === roundNum;
};

export const appendTimelineText = (
  insertions: TimelineTextInsertion[],
  afterStepIndex: number,
  delta: string,
  roundNum?: number,
  isFinal = false,
  messageId?: string,
): TimelineTextInsertion[] => {
  if (!delta) return insertions;

  const last = insertions[insertions.length - 1];
  // Same round keeps streaming even after later steps. Same slot with a missing
  // roundNum is a rebuild/live handoff ("I" then "'m…"). A new round in the
  // same slot is a later wait update and must stay its own block.
  if (
    last
    && Boolean(last.isFinal) === isFinal
    && shouldContinueTimelineText(last, afterStepIndex, roundNum, messageId)
  ) {
    return [...insertions.slice(0, -1), {
      ...last,
      text: last.text + delta,
      roundNum: last.roundNum ?? roundNum,
      ...((last.messageId ?? messageId) ? { messageId: last.messageId ?? messageId } : {}),
    }];
  }

  return [...insertions, {
    afterStepIndex,
    roundNum,
    text: delta,
    ...(isFinal ? { isFinal: true } : {}),
    ...(messageId ? { messageId } : {}),
  }];
};

/** The answer sits below the work trail; commentary stays where it was said. */
export const placeFinalTimelineTextAfterSteps = (
  insertions: TimelineTextInsertion[],
  stepCount: number,
  finalAnswerConfirmed: boolean,
): TimelineTextInsertion[] => {
  if (!finalAnswerConfirmed) return insertions;
  return insertions.map((insertion) => (
    insertion.isFinal ? { ...insertion, afterStepIndex: stepCount } : insertion
  ));
};

export const inFlightTextStepIndex = (
  steps: ThinkingStep[],
  place: 'opening' | 'afterWork' = 'opening',
): number => {
  if (place === 'afterWork') return steps.length;
  const firstTool = steps.findIndex((step) => step.type === 'tool');
  return firstTool === -1 ? steps.length : firstTool;
};

const stepRoundNum = (step: ThinkingStep): number => (
  Number(step.round_num ?? step.data?.round_num ?? 0)
);

const activityReasoningRound = (steps: ThinkingStep[]): number | null => {
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    if (steps[index].type === 'reasoning') return stepRoundNum(steps[index]);
  }
  return null;
};

// A wait belongs after the round that decided to wait, not before its thought.
// Inserting it at the round's first step put "Waiting for agents..." above the
// wait-round chip, so the foot showed both, and that chip stayed the live tail
// after the report — still labelled with the wait-round headline.
export const timelineStepIndexAfterRound = (
  steps: ThinkingStep[],
  roundNum: number,
): number => {
  let last = -1;
  for (let index = 0; index < steps.length; index += 1) {
    if (stepRoundNum(steps[index]) <= roundNum) last = index;
  }
  return last + 1;
};

// Where a round's own commentary belongs: below the reasoning that produced it,
// above the tools it announces. A round streams reasoning first, then its text,
// then any tools, so the text sits at that round's first tool. A round that runs
// no tool — it thought, wrote a result, then waited or ended — puts its text
// after all its steps, so "Thought for Ns" stays above the words it wrote
// instead of being buried beneath them. Placing text at the round's first *step*
// dropped it above that round's thinking whenever the round opened by thinking.
export const timelineStepIndexForRoundText = (
  steps: ThinkingStep[],
  roundNum: number,
): number => {
  const firstToolInRound = steps.findIndex(
    (step) => stepRoundNum(step) === roundNum && step.type === 'tool',
  );
  if (firstToolInRound !== -1) return firstToolInRound;
  return timelineStepIndexAfterRound(steps, roundNum);
};

/**
 * Place the text a live turn just streamed, without rebuilding the timeline.
 *
 * The sibling of `timelineInsertionsFromTurnState`: that one derives placement
 * for a whole turn, this one for the delta of a turn already on screen. They
 * have to agree, because a reconnect swaps one for the other mid-answer, so
 * both live here rather than one of them living inline in the stream handler.
 */
export const appendStreamedTimelineText = ({
  insertions,
  steps,
  delta,
  currentRound,
  roundStart,
  finalAnswerConfirmed,
  messageId,
}: {
  insertions: TimelineTextInsertion[];
  steps: ThinkingStep[];
  delta: string;
  currentRound: number | null;
  roundStart: number;
  finalAnswerConfirmed: boolean;
  messageId?: string;
}): TimelineTextInsertion[] => {
  const round = currentRound ?? 0;
  // The answer sits below the work trail. Anything before the turn has done
  // any work is the opening update and sits above it.
  const afterWork = finalAnswerConfirmed || roundStart > 0;
  const afterStepIndex = !finalAnswerConfirmed && afterWork
    ? timelineStepIndexForRoundText(steps, round)
    : inFlightTextStepIndex(steps, afterWork ? 'afterWork' : 'opening');
  // A new message id is a new block, and the model separates blocks with a
  // blank line that would otherwise open the new one.
  const startsNewBlock = Boolean(messageId)
    && messageId !== insertions[insertions.length - 1]?.messageId;
  return appendTimelineText(
    insertions,
    afterStepIndex,
    startsNewBlock ? delta.replace(/^\n\n/, '') : delta,
    round,
    finalAnswerConfirmed,
    messageId,
  );
};

export const addRoundTimelineInsertion = (
  insertions: TimelineTextInsertion[],
  stepOffset: number,
  steps: ThinkingStep[],
  content: string,
  options?: { afterAllSteps?: boolean; isFinal?: boolean },
): TimelineTextInsertion[] => {
  if (!content.trim()) return insertions;
  if (options?.afterAllSteps) {
    // Placement and finality are separate concerns. A stopped turn's last words
    // belong after its steps, but they are not an answer — marking them final
    // lifts them clear of the work trail as though the turn had finished.
    return [...insertions, {
      afterStepIndex: stepOffset + steps.length,
      ...(options.isFinal ? { isFinal: true } : {}),
      text: content,
    }];
  }
  return [...insertions, {
    afterStepIndex: stepOffset + inFlightTextStepIndex(steps, 'opening'),
    text: content,
  }];
};

export const timelineInsertionsFromTurnState = (
  content: string,
  steps: ThinkingStep[],
  roundStart: number,
  currentRound: number | null,
  finalAnswerConfirmed: boolean,
  textSegments?: Array<{
    round_num: number;
    text: string;
    message_id?: string;
    message_phase?: 'commentary' | 'final_answer';
  }>,
): TimelineTextInsertion[] => {
  if (!content) return [];
  if (textSegments?.length) {
    // The backend labels every block it streams, so a labelled turn is taken at
    // its word. Falling back to "the last block is the answer" is only right
    // for messages written before phases existed; applied to a labelled turn it
    // crowns commentary the moment the final round is announced.
    const labelled = textSegments.some((segment) => segment.message_phase);
    const rebuilt = textSegments.reduce<TimelineTextInsertion[]>((insertions, segment, index) => {
      const isFinalSegment = labelled
        ? segment.message_phase === 'final_answer'
        : index === textSegments.length - 1 && finalAnswerConfirmed;
      const afterStepIndex = isFinalSegment
        ? steps.length
        : segment.round_num === 0
          ? inFlightTextStepIndex(steps, 'opening')
          : timelineStepIndexForRoundText(steps, segment.round_num);
      return appendTimelineText(
        insertions,
        afterStepIndex,
        segment.text,
        segment.round_num,
        isFinalSegment,
        segment.message_id,
      );
    }, []);
    return placeFinalTimelineTextAfterSteps(
      rebuilt,
      steps.length,
      finalAnswerConfirmed,
    );
  }
  const start = Math.min(Math.max(roundStart, 0), content.length);
  const before = content.slice(0, start);
  const after = content.slice(start);
  const insertions = addRoundTimelineInsertion([], 0, steps, before);
  if (!after) return insertions;
  if (finalAnswerConfirmed) {
    return addRoundTimelineInsertion(insertions, 0, steps, after, {
      afterAllSteps: true,
      isFinal: true,
    });
  }
  // Kickoff sits on the first tool so live chips render under it. A later
  // wait update (content before round_start) stays after the tools.
  const place = before.trim() ? 'afterWork' : 'opening';
  return appendTimelineText(
    insertions,
    place === 'afterWork'
      ? timelineStepIndexForRoundText(steps, currentRound ?? 0)
      : inFlightTextStepIndex(steps, place),
    after,
    currentRound ?? 0,
  );
};

export const interleaveThinkingAndText = (
  thinkingSteps: ThinkingStep[],
  insertions: TimelineTextInsertion[],
  waitMarkers: TimelineWaitMarker[] = [],
): InterleavedTurnItem[] => {
  const items: InterleavedTurnItem[] = [];

  let activityBuffer: ThinkingStep[] = [];
  let activityStartIndex = 0;

  const flushActivity = () => {
    if (activityBuffer.length === 0) return;
    items.push({
      kind: 'activity',
      id: activityStartIndex,
      steps: activityBuffer,
    });
    activityBuffer = [];
  };

  const pushText = (index: number, text: string, isFinal?: boolean, messageId?: string) => {
    if (!text) return;
    flushActivity();
    const previous = items[items.length - 1];
    if (previous && previous.kind === 'text' && isTimelineTokenContinuation(previous.text, text)) {
      items[items.length - 1] = {
        ...previous,
        isFinal: previous.isFinal || isFinal,
        messageId: previous.messageId ?? messageId,
        text: previous.text + text,
      };
      return;
    }
    items.push({ kind: 'text', id: index, isFinal, messageId, text });
  };

  const timelineEntries = [
    ...insertions.map((insertion, order) => ({
      index: Math.min(Math.max(insertion.afterStepIndex, 0), thinkingSteps.length),
      kind: 'text' as const,
      order,
      roundNum: insertion.roundNum ?? -1,
      isFinal: insertion.isFinal,
      messageId: insertion.messageId,
      text: insertion.text,
    })),
    ...waitMarkers.map((marker, order) => ({
      index: timelineStepIndexAfterRound(thinkingSteps, marker.round_num),
      kind: 'wait' as const,
      order,
      roundNum: marker.round_num,
      marker,
    })),
  ].sort((left, right) => (
    left.index - right.index
    || left.roundNum - right.roundNum
    || (left.kind === right.kind ? left.order - right.order : left.kind === 'text' ? -1 : 1)
  ));
  let timelineEntryIndex = 0;

  const pushTimelineEntriesAt = (index: number) => {
    while (
      timelineEntryIndex < timelineEntries.length
      && timelineEntries[timelineEntryIndex].index === index
    ) {
      const entry = timelineEntries[timelineEntryIndex];
      if (entry.kind === 'text') {
        pushText(index, entry.text, entry.isFinal, entry.messageId);
      } else {
        flushActivity();
        const waitItem: Extract<InterleavedTurnItem, { kind: 'wait' }> = {
          kind: 'wait',
          id: entry.marker.id,
          waiting: Boolean(entry.marker.waiting_for),
          steps: [],
          ...(entry.marker.duration_ms
            ? { durationMs: entry.marker.duration_ms }
            : {}),
        };
        items.push(waitItem);
      }
      timelineEntryIndex += 1;
    }
  };

  for (let index = 0; index < thinkingSteps.length; index += 1) {
    const step = thinkingSteps[index];
    pushTimelineEntriesAt(index);
    if (
      step.type === 'tool'
      && toolCallIsContextFull(step.data as ToolCall)
    ) {
      continue;
    }
    // Never merge or split chips after they have been shown. A group ends when
    // a different tool family arrives, or when thought arrives after this
    // chip's work has finished — closing a chip cannot change what is already
    // in it, so neither reads as a regroup. Same-family tools (plan → read,
    // more of the same search) stay on this chip.
    const incomingFamily = getStepToolFamily(step);
    const currentFamily = getActivityToolFamily(activityBuffer);
    if (step.type === 'reasoning') {
      // Lead thought is its own step in the trail ("Thought for 4s"), including
      // while a specialist is still running. Folding it into the agent chip, or
      // dropping it as wait-round scratchpad, left the Worked list with tools
      // and no record of the thinking that sat between them.
      //
      // A later round's thought is a new chip even when nothing sits between
      // the two reasonings. The wait-round "Providing a brief update" otherwise
      // keeps the label and swallows the think that starts when a specialist
      // reports.
      const priorReasoningRound = activityReasoningRound(activityBuffer);
      if (
        !activityIsOpen(activityBuffer)
        || (
          isSpawnSpecialistFamily(currentFamily)
          && activityHasInFlightTool(activityBuffer)
        )
        || (
          priorReasoningRound !== null
          && priorReasoningRound !== stepRoundNum(step)
        )
      ) {
        flushActivity();
      }
    }
    if (incomingFamily && currentFamily && incomingFamily !== currentFamily) {
      flushActivity();
    }
    // Thought that produced a tool stays on its own chip, the way a search or
    // an agent does, rather than disappearing under that tool's label.
    if (
      incomingFamily
      && activityBuffer.some((item) => item.type === 'reasoning')
      && !getActivityToolFamily(activityBuffer)
    ) {
      flushActivity();
    }

    if (activityBuffer.length === 0) {
      activityStartIndex = index;
    }
    activityBuffer.push(step);
  }

  flushActivity();
  pushTimelineEntriesAt(thinkingSteps.length);

  return items;
};

export const timelineFromCompletedMessage = (
  thinkingSteps: ThinkingStep[] | undefined,
  content: string,
  insertions?: TimelineTextInsertion[],
  waitMarkers: TimelineWaitMarker[] = [],
): InterleavedTurnItem[] => {
  const steps = thinkingSteps ?? [];
  if (insertions && insertions.length > 0) {
    return interleaveThinkingAndText(steps, insertions, waitMarkers);
  }
  const trimmed = content.trim();
  return interleaveThinkingAndText(
    steps,
    trimmed ? [{ afterStepIndex: steps.length, text: content }] : [],
    waitMarkers,
  );
};

const lastAnswerTextIndex = (items: InterleavedTurnItem[]): number => {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (item.kind === 'text' && item.text.trim()) {
      return index;
    }
  }
  return -1;
};

// Final segments keep a slug-rooted continuity key so smooth streaming survives
// the swap from live ids to persisted ids. With more than one segment the key
// still has to be distinct per segment, so it carries the ordinal.
export const FINAL_CONTINUITY_SEGMENT_PREFIX = 'final';

export const finalContinuitySegment = (ordinal: number): string =>
  ordinal === 0
    ? FINAL_CONTINUITY_SEGMENT_PREFIX
    : `${FINAL_CONTINUITY_SEGMENT_PREFIX}-${ordinal}`;

export const isFinalContinuitySegment = (segment: string | undefined): boolean =>
  segment === FINAL_CONTINUITY_SEGMENT_PREFIX ||
  Boolean(segment?.startsWith(`${FINAL_CONTINUITY_SEGMENT_PREFIX}-`));

const explicitFinalTextIndices = (items: InterleavedTurnItem[]): number[] => {
  const indices: number[] = [];
  items.forEach((item, index) => {
    if (item.kind === 'text' && item.isFinal && item.text.trim()) {
      indices.push(index);
    }
  });
  return indices;
};

/**
 * The Sources list belongs after the response, not beside the typer.
 *
 * The backend often finishes — and the message is committed — while the UI is
 * still revealing the answer a character at a time. Showing sources from the
 * full text in that window puts the bibliography above unread sentences.
 * Stopping is the other way the answer is considered finished: the rest snaps
 * onto the screen, so sources can come with it.
 */
export const shouldShowFinalAnswerSources = ({
  finalAnswerRevealed,
  hasFinalAnswer,
  isStreaming,
  stopRequested,
}: {
  finalAnswerRevealed: boolean;
  hasFinalAnswer: boolean;
  isStreaming: boolean;
  stopRequested: boolean;
}): boolean => {
  if (stopRequested) return true;
  if (isStreaming) return false;
  return !hasFinalAnswer || finalAnswerRevealed;
};

export const shouldCollapseWorkTrail = (
  items: InterleavedTurnItem[],
  isStreaming: boolean,
  finalAnswerConfirmed = false,
  stopped = false,
): boolean => {
  // Mid-turn kickoff sits in front of live chips; the confirmed answer sits
  // after the work trail. Collapse to "Worked" after the backend confirms
  // the final answer, after the turn is persisted, or after Stop.
  //
  // Stop still collapses so long reasoning does not fill the chat. Commentary
  // and chips sit inside Worked — expand the toggle to read them. They are
  // not promoted to an answer.
  if (!items.some((item) => item.kind === 'activity')) return false;
  if (finalAnswerConfirmed || stopped) return true;
  if (isStreaming) return false;
  return lastAnswerTextIndex(items) !== -1;
};

export const partitionLiveActivities = (
  items: InterleavedTurnItem[],
  isStreaming = false,
): {
  settled: InterleavedTurnItem[];
  live: InterleavedTurnItem[];
} => {
  const settled: InterleavedTurnItem[] = [];
  const live: InterleavedTurnItem[] = [];
  for (const item of items) {
    if (item.kind === 'activity' && item.steps.some((step) => isLiveToolStep(step, isStreaming))) {
      live.push(item);
    } else {
      settled.push(item);
    }
  }
  return { settled, live };
};

const activityHasTool = (item: InterleavedTurnItem): boolean => (
  item.kind === 'activity' && item.steps.some((step) => step.type === 'tool')
);

export const pinLiveActivitiesAboveAnswer = (
  items: InterleavedTurnItem[],
): InterleavedTurnItem[] => {
  // The lead speaks before its agents appear, so a tool chip that arrived
  // before the turn had said anything drops below those first words — live or
  // settled, so a reload matches what the lawyer watched.
  //
  // Thought is not an agent. The reasoning that produced the opening line
  // belongs above it; treating it like an unannounced spawn would put
  // "Thought for 7s" under the sentence that thought wrote.
  //
  // An agent that already sits under the text introducing it is left alone. It
  // belongs to that round, and gathering every live agent into one strip would
  // drag one started later up beside the ones the turn opened with — above the
  // wait it followed and the commentary that announced it — only to drop back
  // into place once it reported.
  const firstTextIndex = items.findIndex(
    (item) => item.kind === 'text' && item.text.trim(),
  );
  if (firstTextIndex <= 0) return items;

  const unannounced: InterleavedTurnItem[] = [];
  const rest: InterleavedTurnItem[] = [];
  items.forEach((item, index) => {
    if (index < firstTextIndex && activityHasTool(item)) {
      unannounced.push(item);
    } else {
      rest.push(item);
    }
  });
  if (unannounced.length === 0) return items;

  const anchor = firstTextIndex - unannounced.length;
  return [
    ...rest.slice(0, anchor + 1),
    ...unannounced,
    ...rest.slice(anchor + 1),
  ];
};

/**
 * Where the timeline stops until the text above has finished appearing.
 *
 * The lead's commentary and the tools it announces reach the client together,
 * but a chip paints whole while the text is still being revealed a character
 * at a time — so the work appears to happen before the sentence introducing
 * it. Everything below the first block still revealing is held back.
 *
 * Returns -1 when nothing is waiting, so the caller renders the lot.
 */
export const timelineRevealGateIndex = (
  items: InterleavedTurnItem[],
  isTextRevealed: (index: number) => boolean,
): number => items.findIndex((item, index) => (
  item.kind === 'text' && Boolean(item.text.trim()) && !isTextRevealed(index)
));

export const splitWorkAndFinalAnswer = (
  items: InterleavedTurnItem[],
  { stopped = false, streaming = false }: { stopped?: boolean; streaming?: boolean } = {},
): {
  workItems: InterleavedTurnItem[];
  finalTexts: Extract<InterleavedTurnItem, { kind: 'text' }>[];
} => {
  // A final round can emit several output messages. Every one of them is part
  // of the answer, so all of them are lifted out in order — keeping only the
  // last buried the opening of the answer in the collapsed work trail.
  let answerIndices = explicitFinalTextIndices(items);
  if (answerIndices.length === 0) {
    // Completed legacy messages predate explicit phases, so retain the old
    // last-text fallback only when no marked final segment exists.
    //
    // A turn still running is never legacy: the backend marks the answer as it
    // streams, and it announces the final round before the first word of it
    // arrives. In that gap the newest commentary is the last text there is, and
    // crowning it drops it below the work trail until the real answer arrives
    // and takes the title back — the block visibly jumps. A stopped turn is
    // likewise not legacy: it is known to have no answer, so its last piece of
    // commentary must stay in the work trail rather than pose as one.
    const fallbackIndex = stopped || streaming ? -1 : lastAnswerTextIndex(items);
    if (fallbackIndex === -1) {
      return { workItems: items, finalTexts: [] };
    }
    answerIndices = [fallbackIndex];
  }

  const answers = new Set(answerIndices);
  return {
    workItems: items.filter((_item, index) => !answers.has(index)),
    finalTexts: answerIndices.map(
      (index) => items[index] as Extract<InterleavedTurnItem, { kind: 'text' }>,
    ),
  };
};

// What a reader would call "the response": only the segments the timeline puts
// below the work trail. The stored content also carries the commentary the lead
// wrote while working, which belongs on screen but not in a copy or an export.
export const answerTextFromTimeline = (
  items: InterleavedTurnItem[],
  options?: { stopped?: boolean },
): string =>
  splitWorkAndFinalAnswer(items, options)
    .finalTexts.map((item) => item.text.trim())
    .filter(Boolean)
    .join('\n\n');

// Structural rather than the Message type from useChat, which imports from here.
export interface AnswerBearingMessage {
  content: string;
  thinkingSteps?: ThinkingStep[];
  timelineInsertions?: TimelineTextInsertion[];
  message_metadata?: {
    generation_stopped?: boolean;
    stream_interrupted?: boolean;
  } | null;
}

// Empty when the turn never reached an answer. An interrupted turn has only the
// commentary it wrote on the way, and that is the work trail, not a response —
// callers should offer nothing to copy rather than hand back the narration.
export const answerTextFromMessage = (message: AnswerBearingMessage): string =>
  answerTextFromTimeline(
    timelineFromCompletedMessage(
      message.thinkingSteps,
      message.content,
      message.timelineInsertions,
    ),
    {
      stopped: Boolean(
        message.message_metadata?.generation_stopped
        || message.message_metadata?.stream_interrupted
      ),
    },
  );
